import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, ArrowRight, BadgeCheck } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { prefillService } from "@/lib/site-data";

const img = (id, w = 1200) => `https://images.unsplash.com/${id}?auto=format&fit=crop&q=80&w=${w}`;

const POINTS = [
  "Foundation excavation & waterproof membrane systems",
  "Interior drainage, sump systems & moisture control",
  "Crack repair and water intrusion stopped at the source",
  "Lifetime warranty options on qualifying systems",
];

export default function WaterproofingFeature() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const floatY = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section id="waterproofing" ref={ref} data-testid="waterproofing-section" className="relative bg-black py-28 md:py-36 overflow-hidden">
      <div className="absolute -top-32 -left-32 h-[420px] w-[420px] rounded-full bg-[#0066FF]/20 blur-[120px]" />
      <div className="absolute bottom-0 right-0 h-[380px] w-[380px] rounded-full bg-[#00E5FF]/10 blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-16 items-center">
        <Reveal>
          <Overline>02 — Waterproofing</Overline>
          <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
            Water Finds a Way.
            <span className="block bg-gradient-to-r from-[#00E5FF] to-[#0066FF] bg-clip-text text-transparent">
              We Make Sure It Doesn't.
            </span>
          </h2>
          <p className="mt-6 text-gray-400 text-base md:text-lg leading-relaxed max-w-xl">
            Water intrusion can damage foundations, basements, concrete, and the long-term value of a
            property. Phillips identifies the source and installs solutions designed to stop the problem
            at its source.
          </p>
          <ul className="mt-8 space-y-3.5">
            {POINTS.map((p) => (
              <li key={p} className="flex items-start gap-3 text-gray-300">
                <span className="mt-0.5 rounded-full bg-[#0066FF]/20 p-1">
                  <Check size={14} className="text-cyan-400" />
                </span>
                <span className="text-sm md:text-base">{p}</span>
              </li>
            ))}
          </ul>
          <a
            href="#estimate"
            data-testid="waterproofing-inspection-btn"
            onClick={() => prefillService("Basement Waterproofing")}
            className="mt-10 inline-flex items-center gap-2 rounded-md bg-[#0066FF] px-8 py-4 font-display text-sm font-semibold tracking-widest text-white transition-[background-color,box-shadow,color] duration-300 hover:bg-[#00E5FF] hover:text-black hover:shadow-[0_0_35px_rgba(0,229,255,0.55)]"
          >
            SCHEDULE A WATERPROOFING INSPECTION <ArrowRight size={16} />
          </a>
        </Reveal>

        <div className="relative">
          <Reveal delay={0.15}>
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_0_60px_rgba(0,102,255,0.25)]">
              <img
                src={img("photo-1704883038327-e9cd4c164455")}
                alt="Waterproofing installation by Phillips crew"
                loading="lazy"
                className="w-full h-[420px] md:h-[520px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </div>
          </Reveal>
          <motion.div
            style={{ y: floatY }}
            className="absolute -bottom-10 -left-4 md:-left-10 w-44 md:w-56 rounded-xl overflow-hidden border-2 border-[#0066FF]/60 shadow-[0_0_40px_rgba(0,102,255,0.4)]"
          >
            <img src={img("photo-1692362385851-cf68a6d9604b", 600)} alt="Foundation excavation" loading="lazy" className="h-32 md:h-40 w-full object-cover" />
          </motion.div>
          <div className="absolute -top-5 -right-3 md:-right-6 glass-dark rounded-xl px-5 py-4 flex items-center gap-3 shadow-[0_0_30px_rgba(0,0,0,0.5)]">
            <BadgeCheck size={26} className="text-cyan-400" />
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400">Lifetime Warranty</p>
              <p className="text-sm font-bold">Available</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
